import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AccountManager,
  BetManager,
  Bus,
  constants,
  getConfigProp,
  isEmptyCollection,
  Logger,
  ObservableOddsProvider,
  SiteConfigManager,
  useApplicationState,
  useBetslipState,
  useBetslipStateUpdate,
  useBonusBoost,
  useGlobalBetslipState,
} from '@atlas/core';
import { staticRoutes } from '@atlas/routing';
import { BetslipCommonError, BonusBoost, Button, Label } from '@atlas/ui';

import NotificationStatePopup from 'common/Popup/NotificationStatePopup';
import { debounce } from 'common/utils/debounce';
import BetCheckerButton from 'components/BetChecker/BetCheckerButton';
import { useSignupPopup } from 'src/components/Auth/useSignUpPopup';

import BetsList from './BetItemsList';
import BetslipBetBooking from './BetslipBetBooking';
import {
  BetslipPreOrderCreateBtn,
  BetslipPreOrderReducer,
  BetslipPreOrderReport,
} from './BetslipPreOrder';
import BonusBoostPopup from './BonusBoostPopup';
import { useLocalBetslip } from './LocalBetslipContext';
import OrderBetslipControls from './OrderBetslipControls';
import DefaultControls from './RegularControls';
import usePublicBet from './usePublicBet';
import useSelectedBonuses from './useSelectedBonuses';
import validate from './validator';

export const emptySelectedBet = {};

export const validateSelectedBet = debounce((selectedBet, state) => {
  // Invoking client validation of selected bet
  const clientValidationError = validate(selectedBet, state);

  if (clientValidationError) {
    Bus.emit(Bus.events.bet.validationError, clientValidationError);
  } else {
    BetManager.validateBets([selectedBet]);
  }
}, 1000);

const BetslipClassic = () => {
  const {
    active,
    amount,
    betItems,
    betTypes,
    bonuses,
    fullfited,
    loading,
    settings,
    validationResult,
    validationStatus,
  } = useBetslipState();
  const { reports } = useGlobalBetslipState();
  const { isOrderBet, setIsOpenPopup } = useLocalBetslip();
  const { authenticated } = useApplicationState();
  const currency = AccountManager.getAccountCurrency();

  const isBetItemsAvailable = betItems.length > 0;

  const { openSignupPopup } = useSignupPopup();

  const signUpPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);
  const isSportRadarProject = getConfigProp('isSportRadarProject');

  const buttonsNotificationPopup = (
    <>
      <Link className="secondary" to={staticRoutes.signin}>
        <Button label="Sign in" onClick={() => setIsOpenPopup(false)} size="big" type="secondary" />
      </Link>

      {SiteConfigManager.getConfig('config.config.enable_registration') &&
        (signUpPopup ? (
          <Button
            className="mybets-signup-button"
            label="Sign up"
            onClick={openSignupPopup}
            size="big"
            type="primary"
          />
        ) : (
          <Link className="primary" to={staticRoutes.signup}>
            <Button
              label="Sign up"
              onClick={() => setIsOpenPopup(false)}
              size="big"
              type="primary"
            />
          </Link>
        ))}
    </>
  );

  const betslipStateUpdate = useBetslipStateUpdate();
  const { removeSelectedBonuses, selectedFreebet } = useSelectedBonuses();

  const { selectedBetTypeIndex, setSelectedBetTypeIndex } = useLocalBetslip();

  usePublicBet(setSelectedBetTypeIndex);

  // Auto reset side effect for selected bet type index
  useEffect(() => {
    if (!isEmptyCollection(betTypes) && betTypes.length - 1 < selectedBetTypeIndex) {
      setSelectedBetTypeIndex(betTypes.length - 1);
    }
  }, [betTypes, selectedBetTypeIndex]);

  const selectedBet = betTypes[selectedBetTypeIndex] || betItems[0] || emptySelectedBet;

  useEffect(() => {
    removeSelectedBonuses();

    if (selectedBet.requestId) {
      // Resetting validation status
      betslipStateUpdate({
        validationStatus: constants.BETSLIP_VALIDATION_STATUS.WAITING_VALIDATION,
      });
    } else {
      // Canceling validation on unselect
      validateSelectedBet.cancel();
    }
  }, [selectedBet.requestId]);

  // Betslip validation ready effect
  useEffect(() => {
    // Betslip settings check
    if (settings.validationDisabled) {
      return;
    }
    // validating only if authenticated
    // bets data fetched
    // bets active
    // betslip not loading
    if (authenticated && fullfited && active && !loading) {
      // Verifying if selected bet exist
      if (isEmptyCollection(selectedBet)) {
        return;
      }

      // Checking if betslip waits for validation
      if (validationStatus === constants.BETSLIP_VALIDATION_STATUS.WAITING_VALIDATION) {
        const { odds, requestId } = selectedBet;

        // Skipping if bet not ready or its the same selected bet validation
        if (requestId && odds) {
          Logger?.debug('validating selected set', selectedBet);
          validateSelectedBet.cancel();
          // Invoking validation of selected bet
          validateSelectedBet(selectedBet, {
            betItems,
            settings,
          });
        }
      }
    }
  }, [
    validationStatus,
    authenticated,
    fullfited,
    active,
    loading,
    selectedBet,
    settings,
    betItems,
  ]);
  const [isOpenBonusBoostPopup, setOpenBonusBoostPopup] = useState(false);

  const handleOpenBonusBoostPopup = useCallback(() => {
    setOpenBonusBoostPopup(isOpenBonusBoostPopup => !isOpenBonusBoostPopup);
  }, [setOpenBonusBoostPopup]);

  const bonusBoost = useBonusBoost(
    isSportRadarProject ? [] : bonuses[selectedBet.requestId] || [],
    betItems,
  );

  useLayoutEffect(() => {
    if (bonusBoost) {
      const element = document.querySelector('.bet-items');

      if (element) {
        element.scrollTop = element.scrollHeight;
      }
    }
  }, [bonusBoost]);

  const bonusBoostBadges = useMemo(() => {
    const badges = [];

    if (bonusBoost) {
      badges.push({ text: 'comboboost' });

      const { minOdds } = bonusBoost.currentBonusRule;

      if (minOdds) {
        badges.push({ params: { minOdds }, text: 'min_odds' });
      }
    }

    return badges;
  }, [bonusBoost]);

  const minOddsForBonusBoost = useMemo(() => {
    if (selectedFreebet || !bonusBoost) {
      return undefined;
    }

    return bonusBoost.currentBonusRule.minOdds;
  }, [bonusBoost, selectedFreebet]);

  const isPreOrderBetAllowed = SiteConfigManager.getConfig('config.config.betslip.showPreOrder');
  const [preOrderState, preOrderDispatch] = BetslipPreOrderReducer();
  const preOrderReportBets = preOrderState.result && preOrderState.result.bets;
  const isShowPreOrderBtn = isPreOrderBetAllowed && !authenticated;
  const isBetCheckerAvailable = SiteConfigManager.getConfig(
    'config.config.sportsbook.enableBetChecker',
  );

  if (preOrderReportBets) {
    return (
      <BetslipPreOrderReport
        bets={preOrderReportBets}
        preOrderDispatch={preOrderDispatch}
        preorderId={preOrderState.result.id}
      />
    );
  }

  if (isEmptyCollection(betItems)) {
    if (!isEmptyCollection(reports)) {
      return null;
    }

    return (
      <Suspense fallback={<div />}>
        <NotificationStatePopup
          absolute={false}
          buttons={!authenticated && buttonsNotificationPopup}
          message="No bets have been selected. To select a bet click in a respective odd."
          title="You have not added outcomes"
        />
        {isPreOrderBetAllowed && (
          <BetslipBetBooking setSelectedBetTypeIndex={setSelectedBetTypeIndex} />
        )}
        {isBetCheckerAvailable && <BetCheckerButton onClick={() => setIsOpenPopup(false)} />}
      </Suspense>
    );
  }

  const selectedBetValidationResult = validationResult[selectedBet?.requestId];

  // Check if vip mode enabled
  const isVipAllowed = selectedBetValidationResult?.isVipAllowed;
  const vipModeEnabled =
    isVipAllowed && amount > selectedBetValidationResult?.betLimits?.finalMaxBetLimit;

  // Check if vip mode contain errors
  // Verifing that bet amount does not exceed the max bet referral value
  const betReferralError = vipModeEnabled && amount > selectedBetValidationResult?.maxBetReferral;

  return (
    <ObservableOddsProvider bets={betItems}>
      <div className="betslip-classic-container">
        <div className="bet-items">
          {isBetItemsAvailable && (
            <div className="bets-list">
              <BetsList minOdds={bonusBoost && (minOddsForBonusBoost || 1)} />
            </div>
          )}
          {bonusBoost && !selectedFreebet && !isOrderBet && (
            <div className="bonus_boost-wrapper">
              <BonusBoost
                badges={bonusBoostBadges}
                items={bonusBoost.items}
                onInfoClick={handleOpenBonusBoostPopup}
              >
                {bonusBoost.nextBonusRule ? (
                  <Label
                    message="bonus_boost_hint"
                    params={{
                      bonusPercentage: `${(bonusBoost.nextBonusRule.amount * 100 - 100).toFixed(
                        0,
                      )}%`,
                      outcomeNumber:
                        bonusBoost.nextBonusRule.outcomesNumber - bonusBoost.matchedOutcomesNumber,
                    }}
                  />
                ) : (
                  <Label message="bonus_boost_max_outcomes_error" />
                )}
              </BonusBoost>
              {isOpenBonusBoostPopup && (
                <BonusBoostPopup bonusBoost={bonusBoost} onClose={handleOpenBonusBoostPopup} />
              )}
            </div>
          )}
        </div>

        {
          // Displaying message that vip place bet availabe
          isVipAllowed && !isOrderBet && (
            <div className="betslip-bet-report__header">
              <div className="betslip-message">
                <div className="emoji-icon">
                  {/* Gem stone emoji */}
                  &#128142;
                </div>

                <Label
                  className="bet-report-header__label"
                  message="betslip_place_vip_bet_available"
                  params={selectedBetValidationResult}
                />
              </div>
            </div>
          )
        }

        {
          // Show max bet refferal error once amout is too big
          // Currently using for vip place bet validation
          betReferralError && (
            <div className="betslip-bet-report__header">
              <BetslipCommonError code="max_bet_referral" params={selectedBetValidationResult} />
            </div>
          )
        }

        <Suspense fallback={<div />}>
          {isOrderBet ? (
            <OrderBetslipControls currency={currency} />
          ) : (
            <DefaultControls
              currency={currency}
              isShowPreOrderBtn={isShowPreOrderBtn}
              placeBetDisabled={betReferralError}
              selectedBetTypeIndex={selectedBetTypeIndex}
              setSelectedBetTypeIndex={setSelectedBetTypeIndex}
              vip={vipModeEnabled}
            />
          )}
        </Suspense>

        {isShowPreOrderBtn && (
          <BetslipPreOrderCreateBtn
            amount={amount}
            preOrderDispatch={preOrderDispatch}
            preOrderState={preOrderState}
            selectedBet={selectedBet}
          />
        )}
      </div>
    </ObservableOddsProvider>
  );
};

export default BetslipClassic;
